import { useEffect, useState } from 'react'
import { api, type FormSettings } from '../lib/api.ts'
import { formRows, type FormRow } from '../lib/formSettings.ts'
import { Button } from '../components/Button.tsx'
import { DataTable, type Column } from '../components/DataTable.tsx'
import { FormSettingsTabs } from '../components/FormSettingsTabs.tsx'
import { Tag } from '../components/Tag.tsx'
import { icons } from '../components/icons.ts'

/**
 * The pharmaceutical forms, and the names each is found by (#607, ADR 0041).
 *
 * Shaped like the products list: a row per form, and the form's own page for
 * its names -- the built-in ones, read-only, beside the ones this account
 * taught. A name waiting for approval is work, so the forms with one come
 * first and say how many.
 *
 * The account is the session's, as on the products screen: a super admin
 * looking at another pharmacy switches account in the header.
 */
export function Forms({
  accountId,
  onOpenForm,
  onBack,
}: {
  /** Undefined until the session has answered. */
  accountId: string | undefined
  onOpenForm: (form: string) => void
  onBack: () => void
}) {
  const [settings, setSettings] = useState<FormSettings | undefined>()
  const [error, setError] = useState<string | undefined>()
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    if (accountId === undefined) return
    let live = true
    setError(undefined)
    void api
      .formSettings(accountId)
      .then((next) => {
        if (live) setSettings(next)
      })
      .catch((cause: unknown) => {
        if (live) setError(cause instanceof Error ? cause.message : 'falha ao carregar')
      })
    return () => {
      live = false
    }
  }, [accountId, attempt])

  return (
    <div className="flex flex-col gap-5 p-5 pt-0">
      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="tertiary"
          tone="neutral"
          size="small"
          leadingIcon={icons.back}
          onClick={onBack}
        >
          Fila
        </Button>
        <h1 className="text-tH4 text-text-strong">Formas farmacêuticas</h1>
      </div>

      <FormSettingsTabs active="forms" />

      <p className="text-tP text-text-weak max-w-prose">
        Os nomes pelos quais cada forma é reconhecida na receita. Os nomes fixos valem para todas as
        farmácias; abra uma forma para ver os desta farmácia e aprovar os que estão esperando.
      </p>

      <DataTable
        columns={COLUMNS}
        rows={settings === undefined ? [] : formRows(settings)}
        rowKey={(row) => row.id}
        loading={settings === undefined && error === undefined}
        error={error}
        onRetry={() => setAttempt((n) => n + 1)}
        onRowClick={(row) => onOpenForm(row.id)}
        pageSize={12}
      />
    </div>
  )
}

/** The first few built-in names, so a row says what the form is called without a wall of tags. */
const SHOWN_NAMES = 4

const COLUMNS: ReadonlyArray<Column<FormRow>> = [
  { key: 'form', header: 'Forma', render: (row) => row.label },
  {
    key: 'builtIn',
    header: 'Nomes fixos',
    className: 'text-text-weak',
    render: (row) =>
      row.builtInNames.length <= SHOWN_NAMES
        ? row.builtInNames.join(', ')
        : `${row.builtInNames.slice(0, SHOWN_NAMES).join(', ')} e mais ${row.builtInNames.length - SHOWN_NAMES}`,
  },
  {
    key: 'approved',
    header: 'Sinônimos da farmácia',
    className: 'tabular-nums',
    render: (row) =>
      row.approved === 0 ? <span className="text-text-weak">—</span> : String(row.approved),
  },
  {
    key: 'pending',
    header: 'Aguardando aprovação',
    render: (row) =>
      row.pending === 0 ? (
        <span className="text-text-weak">—</span>
      ) : (
        <Tag tone="warning">{row.pending === 1 ? '1 sinônimo' : `${row.pending} sinônimos`}</Tag>
      ),
  },
]
